package com.eladfinish.bible.contest.ui.components.dialogs

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.ShapeDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.cancel
import com.eladfinish.bible.contest.colorAccent
import com.eladfinish.bible.contest.ok
import com.eladfinish.bible.contest.ui.layout.heightAboveKeyboard
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.desc

enum class InputDialogMode {
    CREATE, // new name
    RENAME  // rename existing
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DialogWithInputField(
    label: String,
    mode: InputDialogMode,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit,
    initialValue: String = "",
) {
    BasicAlertDialog(
        onDismissRequest = onDismiss,
    ) {
        OutlinedCard(
            modifier = Modifier.width(320.dp).heightIn(max = 250.dp).heightAboveKeyboard(),
            shape = ShapeDefaults.Medium,
            colors = CardDefaults.elevatedCardColors(),
            elevation = CardDefaults.elevatedCardElevation(),
        ) {
            val accentColor = colorResource(SharedRes.colors.colorAccent)
            val focusRequester = remember { FocusRequester() }

            val textState = rememberSaveable(stateSaver = TextFieldValue.Saver) {
                mutableStateOf(
                    TextFieldValue(
                        text = initialValue,
                        selection = TextRange(0, initialValue.length) // Select all initially
                    )
                )
            }

            // File-name constraints (common invalid characters on Windows; also avoid newlines)
            val invalidChars = remember { setOf('\\', '/', ':', '*', '?', '"', '<', '>', '|', '\n', '\r', '\t') }

            fun validate(input: String): String? {
                val trimmed = input.trim()
                if (trimmed.isEmpty()) return "יש להזין שם"
                if (trimmed.any { it in invalidChars }) return "השם מכיל תווים לא חוקיים"

                if (mode == InputDialogMode.RENAME &&
                    trimmed == initialValue.trim()
                ) {
                    return "לא בוצע שינוי בשם"
                }

                return null
            }

            val currentText = textState.value.text
            val errorMessage = remember(currentText, initialValue) {
                validate(currentText)
            }

            val isConfirmEnabled = remember(currentText, initialValue) {
                errorMessage == null
            }

            val submit = {
                if (isConfirmEnabled) {
                    val value = textState.value.text.trim()
                    onDismiss()
                    onConfirm(value)
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                OutlinedTextField(
                    modifier = Modifier
                        .fillMaxWidth()
                        .focusRequester(focusRequester),
                    value = textState.value,
                    onValueChange = { textState.value = it },
                    textStyle = MaterialTheme.typography.bodyLarge.copy(fontSize = 16.sp),
                    label = { Text(text = label, style = MaterialTheme.typography.bodySmall) },
                    isError = errorMessage != null,
                    supportingText = {
                        if (errorMessage != null) {
                            Text(
                                text = errorMessage,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.error,
                            )
                        }
                    },
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedLabelColor = accentColor,
                        cursorColor = accentColor,
                        focusedBorderColor = accentColor,
                    ),
                    keyboardOptions = KeyboardOptions.Default.copy(
                        imeAction = ImeAction.Done,
                        keyboardType = KeyboardType.Text,
                    ),
                    keyboardActions = KeyboardActions(
                        onDone = { if (isConfirmEnabled) submit() }
                    ),
                    trailingIcon = {
                        if (textState.value.text.isNotEmpty()) {
                            IconButton(onClick = { textState.value = TextFieldValue("") }) {
                                Icon(
                                    imageVector = Icons.Filled.Clear,
                                    contentDescription = "Clear"
                                )
                            }
                        }
                    },
                )

                Spacer(modifier = Modifier.weight(1f))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End
                ) {
                    OutlinedButton(onClick = onDismiss) {
                        Text(SharedRes.strings.cancel.desc().localized())
                    }
                    Spacer(modifier = Modifier.size(12.dp))
                    OutlinedButton(
                        onClick = { submit() },
                        enabled = isConfirmEnabled
                    ) {
                        Text(SharedRes.strings.ok.desc().localized())
                    }
                }
            }

            LaunchedEffect(Unit) {
                focusRequester.requestFocus()
                // Re-apply select-all on open (helpful on some platforms)
                val t = textState.value.text
                textState.value = textState.value.copy(selection = TextRange(0, t.length))
            }
        }
    }
}
